import { HttpClient } from '../http/HttpClient';
import { HttpError } from '../http/HttpError';
import { fetchUserAttributes } from 'aws-amplify/auth';

/**
 * Form Submission API Service - Frontend service for contested case form submissions
 */
class FormSubmissionService {
    constructor(config = {}) {
        // Initialize HTTP client
        this.httpClient = new HttpClient({
            baseUrl: config.baseUrl || 'https://app.legalstream.org/api/contested-case-forms',
            onRequest: this.logRequest.bind(this),
            onResponse: this.logResponse.bind(this),
            onError: this.logError.bind(this),
        });
    }

    /**
     * Get current user ID from AWS Cognito
     * @private
     */
    async getUserId() {
        try {
            const attributes = await fetchUserAttributes();
            // Try to get user ID from sub (unique user identifier) or email
            return attributes.sub || attributes.email || 'anonymous';
        } catch (error) {
            console.warn('Failed to get user attributes:', error);
            return 'anonymous';
        }
    }

    /**
     * Transform API response to match expected structure
     * @private
     */
    _transformSubmissionResponse(apiSubmission) {
        try {
            const submissionData = apiSubmission.data || apiSubmission;

            return {
                id: submissionData.id,
                userId: submissionData.userId,
                submittedData: submissionData.submittedData || {},
                createdAt: submissionData.createdAt,
                updatedAt: submissionData.updatedAt,
            };
        } catch (error) {
            console.error('Error transforming submission response:', error);
            return {
                id: (apiSubmission.data || apiSubmission).id || null,
                userId: (apiSubmission.data || apiSubmission).userId || '',
                submittedData: (apiSubmission.data || apiSubmission).submittedData || {},
                createdAt: (apiSubmission.data || apiSubmission).createdAt,
                updatedAt: (apiSubmission.data || apiSubmission).updatedAt,
            };
        }
    }

    /**
     * Submit form (create or update)
     */
    async submitForm(formData, wizardId) {
        try {
            const userId = await this.getUserId();
            const requestBody = {
                submittedData: formData,
                formId: wizardId
            };

            const headers = {
                'X-User-Id': userId
            };

            const apiResponse = await this.httpClient.post('/submissions', requestBody, { headers });

            const createdSubmission = apiResponse.data || apiResponse;
            const transformedSubmission = this._transformSubmissionResponse(createdSubmission);
            return transformedSubmission;
        } catch (error) {
            throw this.enhanceError(error, 'Failed to submit form');
        } finally {
            console.groupEnd();
        }
    }

    /**
     * Validate form data
     */
    validateFormData(formData) {
        try {
            if (typeof formData !== 'object' || formData === null) {
                throw new Error('Form data must be a valid object');
            }
            JSON.stringify(formData);
            return true;
        } catch (error) {
            throw HttpError.validationError(`Invalid form data: ${error.message}`);
        }
    }

    /**
     * Logging methods
     */
    logRequest({ url, config }) {
        console.debug(`Request: ${config.method} ${url}`);

        if (config.body && config.method !== 'GET') {
            console.debug('Request body:', config.body);
        }
    }

    logResponse({ url, config, response }) {
        console.debug(`Response: ${config.method} ${url} - ${response.status}`);
    }

    logError({ url, config, error }) {
        console.error(`Error: ${config.method} ${url} - ${error.message}`);
    }

    /**
     * Enhance error with additional context
     */
    enhanceError(error, context) {
        if (error instanceof HttpError) {
            error.context = context;
            return error;
        }

        return new HttpError(
            error.status || 500,
            `${context}: ${error.message}`,
            { originalError: error }
        );
    }

}

// Default instance
const formSubmissionService = new FormSubmissionService();

// Named exports for convenience
export const submitForm = (formData, wizardId) => formSubmissionService.submitForm(formData, wizardId);
export const validateFormData = (formData) => formSubmissionService.validateFormData(formData);

export { FormSubmissionService };